import { track } from '../../../services/analytics/analytics'

// Types for tracking events
interface BaseEventProperties {
  timestamp?: string
  session_id?: string
  [key: string]: string | string[] | number | boolean | undefined | null
}

interface MainMenuSectionToggleProperties extends BaseEventProperties {
  section_name: string
  action: 'open' | 'close'
  is_mobile_drawer: boolean
  time_since_page_load_ms: number
}

interface MainMenuCollapseToggleProperties extends BaseEventProperties {
  action: 'collapse' | 'expand'
  is_mini_menu: boolean
  is_tablet: boolean
  is_mobile_drawer: boolean
  time_since_page_load_ms: number
}

interface MainMenuOptionClickProperties extends BaseEventProperties {
  option_name: string
  option_route: string
  section_name?: string
  is_mini_menu: boolean
  is_mobile_drawer: boolean
  time_since_page_load_ms: number
}

interface MainMenuRequestLeaveCTAHoverProperties extends BaseEventProperties {
  available_options_count: number
  available_options: string[]
  is_mini_menu: boolean
  is_mobile_drawer: boolean
  time_since_page_load_ms: number
}

interface MainMenuRequestLeaveCTAOptionClickedProperties extends BaseEventProperties {
  option_name: string
  option_route: string
  option_index: number
  total_options: number
  is_mini_menu: boolean
  is_mobile_drawer: boolean
  time_since_page_load_ms: number
}

interface MainMenuProfileMenuOpenedProperties extends BaseEventProperties {
  is_mini_menu: boolean
  time_since_page_load_ms: number
  trigger_type: 'hover' | 'click'
}

interface MainMenuProfileMenuMyProfileClickProperties extends BaseEventProperties {
  is_admin: boolean
  destination_route: string
  time_since_page_load_ms: number
}

interface MainMenuProfileMenuAccountSettingsClickProperties extends BaseEventProperties {
  time_since_page_load_ms: number
}

interface MainMenuProfileMenuLogoutClickProperties extends BaseEventProperties {
  time_since_page_load_ms: number
  session_duration_seconds: number
}

// Session tracking state
class SidebarSessionTracker {
  private sessionStartTime: number = Date.now()
  private sessionId = `sidebar_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  private pageLoadTime: number = Date.now()

  // Public tracking methods
  trackSectionToggle(sectionData: {
    sectionName: string
    action: 'open' | 'close'
    isMobileDrawer: boolean
  }): void {
    const properties: MainMenuSectionToggleProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      section_name: sectionData.sectionName,
      action: sectionData.action,
      is_mobile_drawer: sectionData.isMobileDrawer,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('MAIN_MENU_SECTION_TOGGLE', properties)
  }

  trackCollapseToggle(collapseData: {
    action: 'collapse' | 'expand'
    isMiniMenu: boolean
    isTablet: boolean
    isMobileDrawer: boolean
  }): void {
    const properties: MainMenuCollapseToggleProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      action: collapseData.action,
      is_mini_menu: collapseData.isMiniMenu,
      is_tablet: collapseData.isTablet,
      is_mobile_drawer: collapseData.isMobileDrawer,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('MAIN_MENU_COLLAPSE_TOGGLE', properties)
  }

  trackOptionClick(optionData: {
    optionName: string
    optionRoute: string
    sectionName?: string
    isMiniMenu: boolean
    isMobileDrawer: boolean
  }): void {
    const properties: MainMenuOptionClickProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      option_name: optionData.optionName,
      option_route: optionData.optionRoute,
      section_name: optionData.sectionName,
      is_mini_menu: optionData.isMiniMenu,
      is_mobile_drawer: optionData.isMobileDrawer,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('MAIN_MENU_OPTION_CLICK', properties)
  }

  trackRequestLeaveCTAHover(hoverData: {
    availableOptionsCount: number
    availableOptions: string[]
    isMiniMenu: boolean
    isMobileDrawer: boolean
  }): void {
    const properties: MainMenuRequestLeaveCTAHoverProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      available_options_count: hoverData.availableOptionsCount,
      available_options: hoverData.availableOptions,
      is_mini_menu: hoverData.isMiniMenu,
      is_mobile_drawer: hoverData.isMobileDrawer,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('MAIN_MENU_REQUEST_LEAVE_CTA_HOVER', properties)
  }

  trackRequestLeaveCTAOptionClicked(optionData: {
    optionName: string
    optionRoute: string
    optionIndex: number
    totalOptions: number
    isMiniMenu: boolean
    isMobileDrawer: boolean
  }): void {
    const properties: MainMenuRequestLeaveCTAOptionClickedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      option_name: optionData.optionName,
      option_route: optionData.optionRoute,
      option_index: optionData.optionIndex,
      total_options: optionData.totalOptions,
      is_mini_menu: optionData.isMiniMenu,
      is_mobile_drawer: optionData.isMobileDrawer,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('MAIN_MENU_REQUEST_LEAVE_CTA_OPTION_CLICKED', properties)
  }

  trackProfileMenuOpened(menuData: {
    isMiniMenu: boolean
    triggerType: 'hover' | 'click'
  }): void {
    const properties: MainMenuProfileMenuOpenedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      is_mini_menu: menuData.isMiniMenu,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      trigger_type: menuData.triggerType,
    }

    track('MAIN_MENU_PROFILE_MENU_OPENED', properties)
  }

  trackProfileMenuMyProfileClick(profileData: {
    isAdmin: boolean
    destinationRoute: string
  }): void {
    const properties: MainMenuProfileMenuMyProfileClickProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      is_admin: profileData.isAdmin,
      destination_route: profileData.destinationRoute,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('MAIN_MENU_PROFILE_MENU_MY_PROFILE_CLICK', properties)
  }

  trackProfileMenuAccountSettingsClick(): void {
    const properties: MainMenuProfileMenuAccountSettingsClickProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('MAIN_MENU_PROFILE_MENU_ACCOUNT_SETTINGS_CLICK', properties)
  }

  trackProfileMenuLogoutClick(): void {
    const sessionDuration = Math.round((Date.now() - this.sessionStartTime) / 1000)

    const properties: MainMenuProfileMenuLogoutClickProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      session_duration_seconds: sessionDuration,
    }

    track('MAIN_MENU_PROFILE_MENU_LOGOUT_CLICK', properties)
  }

  // Initialize tracking on page load
  init(): void {
    this.pageLoadTime = Date.now()
  }
}

// Create and export singleton instance
export const sidebarTracker = new SidebarSessionTracker()

